import { useEffect, useState } from 'react';
import './Team.css';

interface TeamMember {
  agent_id: string;
  machine: string;
  role: 'lead' | 'member';
  capabilities: string[];
  status: 'online' | 'busy' | 'away' | 'offline';
  current_work_id?: string;
  last_seen: number;
}

interface WorkItem {
  id: string;
  title: string;
  status: 'pending' | 'in_progress' | 'blocked' | 'review' | 'completed';
  assigned_to?: string;
  assigned_by: string;
  estimated_tokens?: number;
  token_consumed: number;
  created_at: number;
  started_at?: number;
  completed_at?: number;
}

interface Sprint {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'cancelled';
  goals: string[];
  token_budget?: number;
  work_items?: WorkItem[];
  created_at: number;
  completed_at?: number;
}

interface Question {
  id: string;
  from_agent: string;
  work_item_id?: string;
  question: string;
  priority: 'blocking' | 'high' | 'normal';
  status: 'pending' | 'answered';
  answer?: string;
  answered_by?: string;
  created_at: number;
}

interface TeamData {
  id: string;
  name: string;
  lead_agent?: string;
  members: TeamMember[];
  repos: string[];
  created_at: number;
}

export default function Team() {
  const [team, setTeam] = useState<TeamData | null>(null);
  const [sprint, setSprint] = useState<Sprint | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const teamId = 'estream-dev'; // Could be dynamic from URL params

  useEffect(() => {
    let isMounted = true;
    
    const fetchTeamData = async (isInitialLoad = false) => {
      try {
        // Only show loading spinner on initial load, not refreshes
        if (isInitialLoad) {
          setLoading(true);
        }
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.estream.dev';

        // Fetch team info
        const teamRes = await fetch(`${apiUrl}/agent/team/${teamId}`);
        if (teamRes.ok && isMounted) {
          const teamData = await teamRes.json();
          setTeam(teamData);
        }

        // Fetch active sprint
        const sprintRes = await fetch(`${apiUrl}/agent/team/${teamId}/sprints`);
        if (sprintRes.ok && isMounted) {
          const sprintData = await sprintRes.json();
          // Get the active sprint
          const activeSprint = sprintData.sprints?.find((s: Sprint) => s.status === 'active');
          if (activeSprint) {
            // Fetch full sprint details with work items
            const detailRes = await fetch(`${apiUrl}/agent/team/${teamId}/sprints/${activeSprint.id}`);
            if (detailRes.ok && isMounted) {
              const detail = await detailRes.json();
              setSprint(detail);
            } else if (isMounted) {
              setSprint(activeSprint);
            }
          }
        }

        // Fetch questions
        const questionsRes = await fetch(`${apiUrl}/agent/team/${teamId}/questions`);
        if (questionsRes.ok && isMounted) {
          const questionsData = await questionsRes.json();
          setQuestions(questionsData.questions || []);
        }

        if (isMounted) {
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch team data');
          setLoading(false);
        }
      }
    };

    fetchTeamData(true); // Initial load
    const interval = setInterval(() => fetchTeamData(false), 10000); // Background refresh

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [teamId]);

  if (loading) return <div className="team-page"><div className="loading">Loading team data...</div></div>;
  if (error) return <div className="team-page"><div className="error">Error: {error}</div></div>;
  if (!team) return <div className="team-page"><div className="error">Team not found</div></div>;

  const workItems = sprint?.work_items || [];
  const totalWork = workItems.length;
  const completedWork = workItems.filter(w => w.status === 'completed').length;
  const inProgressWork = workItems.filter(w => w.status === 'in_progress').length;
  const pendingWork = workItems.filter(w => w.status === 'pending').length;
  const completionPct = totalWork > 0 ? Math.round((completedWork / totalWork) * 100) : 0;

  const tokenConsumed = workItems.reduce((sum, w) => sum + (w.token_consumed || 0), 0);
  const tokenBudget = sprint?.token_budget || 0;
  const tokenPct = tokenBudget > 0 ? Math.round((tokenConsumed / tokenBudget) * 100) : 0;

  const onlineMembers = team.members.filter(m => m.status === 'online' || m.status === 'busy');
  const pendingQuestions = questions.filter(q => q.status === 'pending');

  return (
    <div className="team-page">
      <div className="team-header">
        <h1>{team.name}</h1>
        <div className="team-stats">
          <div className="stat">
            <span className="stat-value">{onlineMembers.length}/{team.members.length}</span>
            <span className="stat-label">Online</span>
          </div>
          <div className="stat">
            <span className="stat-value">{completionPct}%</span>
            <span className="stat-label">Sprint Progress</span>
          </div>
          <div className="stat">
            <span className="stat-value">{pendingQuestions.length}</span>
            <span className="stat-label">Questions</span>
          </div>
        </div>
      </div>

      <div className="team-grid">
        {/* Team Members */}
        <div className="team-section members-section">
          <h2>Team Members</h2>
          <div className="members-list">
            {team.members.map(member => (
              <div key={member.agent_id} className={`member-card ${member.status}`}>
                <div className="member-header">
                  <span className="member-name">{member.machine}</span>
                  {member.role === 'lead' && <span className="badge lead">Lead</span>}
                  <span className={`status-dot ${member.status}`}></span>
                </div>
                <div className="member-capabilities">
                  {member.capabilities.map(cap => (
                    <span key={cap} className="capability">{cap}</span>
                  ))}
                </div>
                {member.current_work_id && (
                  <div className="member-work">
                    Working on: {workItems.find(w => w.id === member.current_work_id)?.title || member.current_work_id.slice(0, 8)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Active Sprint */}
        <div className="team-section sprint-section">
          <h2>Active Sprint</h2>
          {sprint ? (
            <>
              <div className="sprint-header">
                <h3>{sprint.name}</h3>
                <div className="sprint-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${completionPct}%` }}></div>
                  </div>
                  <span>{completedWork}/{totalWork} items</span>
                </div>
              </div>
              
              {sprint.goals && sprint.goals.length > 0 && (
                <div className="sprint-goals">
                  <h4>Goals</h4>
                  <ul>
                    {sprint.goals.map((goal, i) => <li key={i}>{goal}</li>)}
                  </ul>
                </div>
              )}

              {tokenBudget > 0 && (
                <div className="token-usage">
                  <h4>Token Budget</h4>
                  <div className="progress-bar">
                    <div className={`progress-fill ${tokenPct > 90 ? 'warning' : ''}`} style={{ width: `${Math.min(tokenPct, 100)}%` }}></div>
                  </div>
                  <span>{tokenConsumed.toLocaleString()} / {tokenBudget.toLocaleString()} ({tokenPct}%)</span>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">No active sprint</div>
          )}
        </div>

        {/* Work Items Kanban */}
        <div className="team-section kanban-section">
          <h2>Work Items</h2>
          <div className="kanban-board">
            <div className="kanban-column">
              <h3>Pending <span className="count">{pendingWork}</span></h3>
              {workItems.filter(w => w.status === 'pending').map(item => (
                <div key={item.id} className="work-card">
                  <div className="work-title">{item.title}</div>
                  {item.estimated_tokens && (
                    <div className="work-meta">~{(item.estimated_tokens / 1000).toFixed(0)}k tokens</div>
                  )}
                </div>
              ))}
            </div>
            <div className="kanban-column">
              <h3>In Progress <span className="count">{inProgressWork}</span></h3>
              {workItems.filter(w => w.status === 'in_progress').map(item => (
                <div key={item.id} className="work-card in-progress">
                  <div className="work-title">{item.title}</div>
                  {item.assigned_to && <div className="work-assignee">{item.assigned_to}</div>}
                  {item.token_consumed > 0 && (
                    <div className="work-meta">{(item.token_consumed / 1000).toFixed(0)}k tokens used</div>
                  )}
                </div>
              ))}
            </div>
            <div className="kanban-column">
              <h3>Completed <span className="count">{completedWork}</span></h3>
              {workItems.filter(w => w.status === 'completed').map(item => (
                <div key={item.id} className="work-card completed">
                  <div className="work-title">{item.title}</div>
                  {item.token_consumed > 0 && (
                    <div className="work-meta">{(item.token_consumed / 1000).toFixed(0)}k tokens</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Questions Queue */}
        {pendingQuestions.length > 0 && (
          <div className="team-section questions-section">
            <h2>Pending Questions</h2>
            <div className="questions-list">
              {pendingQuestions.map(q => (
                <div key={q.id} className={`question-card priority-${q.priority}`}>
                  <div className="question-header">
                    <span className="question-from">{q.from_agent}</span>
                    <span className={`priority-badge ${q.priority}`}>{q.priority}</span>
                  </div>
                  <div className="question-text">{q.question}</div>
                  {q.work_item_id && (
                    <div className="question-work">
                      Related to: {workItems.find(w => w.id === q.work_item_id)?.title || q.work_item_id.slice(0, 8)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
